import * as THREE from 'three';
import { isRoadFamily, roadVehicleGeometry, type RoadFamily } from './road-vehicles';
import { isMilitaryVehicle, militaryVehicleGeometry, type MilitaryVehicleKind } from './military-vehicles';
import { CELL } from '../core/config';
import { type Building, type Cell, key } from '../world/city';
import type { Vec } from '../destruction/structure';
import { coveredFace, fractureGeometry } from './fracture-geometry';

import { GeometryBuilder } from './geometry-builder';
import { CoplanarQuads } from './coplanar-quads';
import { facadeFittings } from './exterior-style';
import { wildlifeGeometry } from './wildlife';
export { GeometryBuilder } from './geometry-builder';

const dirs = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];
const NUMERALS = [0x3f, 0x06, 0x5b, 0x4f, 0x66, 0x6d, 0x7d, 0x07, 0x7f, 0x6f];
function addressPlaque(g: GeometryBuilder, id: number, x: number, y: number, z: number, color: number): void {
  const digits = String(id + 1),
    width = 1.06 / digits.length;
  g.box(x + 0.04, y + 0.42, z - 0.09, 1.16, 0.48, 0.08, color);
  for (let i = 0; i < digits.length; i++) {
    const mask = NUMERALS[Number(digits[i])],
      xx = x + 0.08 + i * width,
      yy = y + 0.48;
    const bars = [
      [0.02, 0.32, width - 0.05, 0.035],
      [width - 0.035, 0.17, 0.03, 0.15],
      [width - 0.035, 0.015, 0.03, 0.15],
      [0.02, 0, width - 0.05, 0.035],
      [0, 0.015, 0.03, 0.15],
      [0, 0.17, 0.03, 0.15],
      [0.02, 0.16, width - 0.05, 0.035],
    ];
    for (let k = 0; k < 7; k++)
      if (mask & (1 << k)) {
        const [dx, dy, w, h] = bars[k];
        g.box(xx + dx, yy + dy, z - 0.1, w, h, 0.02, 0xf5ecda);
      }
  }
}
function* appendGeometry(to: GeometryBuilder, from: GeometryBuilder): Generator<void> {
  to.append(from);
  yield;
}

/** Opaque exterior plus one optional nearby glass band. Frames remain the
 * contiguous prefix required by reconstruction. */
export function buildingGeometry(
  b: Building,
  cells: ReadonlyMap<number, Cell>,
  origin: Vec,
  detail = true,
  context: ReadonlyMap<number, Cell> = cells,
  glassBand?: { low: number; high: number },
): THREE.BufferGeometry {
  const work = buildingGeometrySteps(b, cells, origin, detail, context, glassBand, Infinity);
  let result = work.next();
  while (!result.done) result = work.next();
  return result.value;
}

/** Prepare off-scene geometry in bounded cell slices. Callers commit the result
 * together with its authoritative cells and collision, never a half-built mesh. */
export function* buildingGeometrySteps(
  b: Building,
  cells: ReadonlyMap<number, Cell>,
  origin: Vec,
  detail = true,
  context: ReadonlyMap<number, Cell> = cells,
  glassBand?: { low: number; high: number },
  chunk = 96,
): Generator<void, THREE.BufferGeometry> {
  const merge = (b.landmark || b.exterior) && cells.size > 64;
  const frame = merge ? new CoplanarQuads() : new GeometryBuilder(),
    facade = merge ? new CoplanarQuads() : new GeometryBuilder(),
    glass = new GeometryBuilder(),
    clearGlass = new GeometryBuilder();
  const occupied = new Map<number, Cell>(),
    structuralCells = new Set<number>();
  let count = 0;
  for (const c of context.values()) {
    const id = key(c.x, c.y, c.z);
    occupied.set(id, c);
    if (['column', 'beam', 'slab', 'roof', 'wall'].includes(c.role ?? 'wall')) structuralCells.add(id);
    if (++count % (chunk * 4) === 0) yield;
  }
  const frameLayers: number[] = new Array(b.h).fill(0),
    facadeLayers: number[] = new Array(b.h).fill(0),
    glassLayers: number[] = new Array(b.h).fill(0);
  const layers = [...cells.values()].sort((a, b) => a.y - b.y);
  const markLayer = (y: number) => {
    frame.sealIfLarge();
    facade.sealIfLarge();
    glass.sealIfLarge();
    clearGlass.sealIfLarge();
    frameLayers[y] = frame.vertexCount;
    facadeLayers[y] = facade.vertexCount;
    glassLayers[y] = glass.vertexCount;
  };
  let currentLayer = -1;
  const flushLayer = () => {
    if (frame instanceof CoplanarQuads) frame.flush();
    if (facade instanceof CoplanarQuads) facade.flush();
    if (currentLayer >= 0) markLayer(currentLayer);
  };
  for (const c of layers) {
    if (currentLayer !== c.y) {
      flushLayer();
      currentLayer = c.y;
    }
    if (++count % chunk === 0) yield;
    const structural = ['column', 'beam', 'slab', 'roof', 'wall'].includes(c.role ?? 'wall');
    const g =
      c.material === 'glass'
        ? glassBand && c.y >= glassBand.low && c.y <= glassBand.high
          ? clearGlass
          : glass
        : structural
          ? frame
          : facade;
    const x = c.x * CELL - origin.x,
      y = c.y * CELL - origin.y,
      z = c.z * CELL - origin.z;
    const visible = dirs.map(
      (direction) =>
        !coveredFace(
          c,
          occupied.get(key(c.x + direction[0], c.y + direction[1], c.z + direction[2])),
          direction,
        ),
    );
    // Hide internal frame-to-frame faces; retain faces revealed during frame-first reconstruction.
    const frameVisible = structural
      ? dirs.map(
          (direction, index) =>
            visible[index] ||
            !structuralCells.has(key(c.x + direction[0], c.y + direction[1], c.z + direction[2])),
        )
      : visible;
    const pitchedRoof =
      c.role === 'roof' &&
      c.material === 'wood' &&
      b.roofLevel !== undefined &&
      c.y > b.roofLevel &&
      !!b.roofSpan;
    if (!frameVisible.some(Boolean) && !pitchedRoof) {
      markLayer(c.y);
      continue;
    }
    // Building-wide tones allow uninterrupted coplanar walls to merge.
    const variation = b.exterior ? 1 : 0.97 + ((c.x * 7 + c.z * 11 + c.y * 3) % 7) * 0.009;
    const shade = new THREE.Color(c.color).multiplyScalar((c.hp < 0.6 ? 0.73 : 1) * variation).getHex();
    if (detail && b.exterior?.addressCell === c.id && !c.fracture)
      addressPlaque(facade, b.id, x, y, z + (c.bounds?.z ?? 0) * CELL, b.exterior.trim);
    if (b.service && c.y === 4 && c.z === 0 && !c.fracture) {
      g.box(x, y + 0.15, z - 0.13, CELL, 0.55, 0.15, b.accent);
      if (c.x === 4) {
        g.box(x + 0.48, y + 0.18, z - 0.17, 0.24, 0.5, 0.05, 0xf0ead5);
        g.box(x + 0.22, y + 0.34, z - 0.17, 0.76, 0.18, 0.05, 0xf0ead5);
      }
    }
    if (c.fracture && b.category !== 'prop') {
      fractureGeometry(g, c, x, y, z, shade, frameVisible);
      markLayer(c.y);
      continue;
    }
    if (b.kind === 'road') {
      const box = c.bounds!;
      g.box(
        x + box.x * CELL,
        y + box.y * CELL,
        z + box.z * CELL,
        box.w * CELL,
        box.h * CELL,
        box.d * CELL,
        shade,
        visible,
      );
      if (c.z === Math.floor(b.d / 2) && c.x % 4 < 2)
        g.box(x, y + 0.012, z + 0.08, CELL * box.w, 0.012, 0.08, b.accent);
      markLayer(c.y);
      continue;
    }
    if (b.kind === 'aircraft') {
      const box = c.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 };
      g.box(
        x + box.x * CELL,
        y + box.y * CELL,
        z + box.z * CELL,
        box.w * CELL,
        box.h * CELL,
        box.d * CELL,
        shade,
        visible,
      );
      markLayer(c.y);
      continue;
    }
    if (c.parts || b.exactGeometry) {
      for (const part of c.parts ?? [c.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 }])
        g.box(
          x + part.x * CELL,
          y + part.y * CELL,
          z + part.z * CELL,
          part.w * CELL,
          part.h * CELL,
          part.d * CELL,
          ('color' in part && part.color !== undefined ? part.color : shade) as number,
        );
      markLayer(c.y);
      continue;
    }
    if (b.kind === 'furniture') {
      const box = c.bounds!,
        xx = x + box.x * CELL,
        zz = z + box.z * CELL,
        w = box.w * CELL,
        d = box.d * CELL,
        h = box.h * CELL;
      if (!detail) {
        g.box(xx, y + box.y * CELL, zz, w, h, d, shade, visible);
        markLayer(c.y);
        continue;
      }
      if (b.furniture === 'bed' || b.furniture === 'sofa') {
        g.box(xx, y, zz, w, 0.22, d, 0x87735e);
        g.box(xx + 0.04, y + 0.22, zz + 0.03, w - 0.08, 0.22, d - 0.06, 0xb7bca8);
        g.box(xx + 0.1, y + 0.44, zz + 0.09, w - 0.2, 0.1, 0.24, 0xe6dfc9);
      } else if (b.furniture === 'shelf') {
        for (let s = 0; s < 4; s++) {
          g.box(xx, y + s * 0.4, zz, w, 0.07, d, shade);
          for (let k = 0; k < 3; k++)
            g.box(
              xx + 0.1 + k * 0.26,
              y + s * 0.4 + 0.08,
              zz + 0.16,
              0.19,
              0.21,
              0.25,
              [0x8a9e87, 0xd0ad79, 0xc39a83][k],
            );
        }
        g.box(xx, y, zz, 0.07, h, d, shade);
        g.box(xx + w - 0.07, y, zz, 0.07, h, d, shade);
      } else if (b.furniture === 'crate') g.box(xx, y, zz, w, h, d, shade);
      else {
        for (const dx of [0, w - 0.08])
          for (const dz of [0, d - 0.08]) g.box(xx + dx, y, zz + dz, 0.08, h - 0.08, 0.08, 0x61716d);
        g.box(xx, y + h - 0.08, zz, w, 0.08, d, shade);
        if (b.furniture === 'desk') {
          g.box(xx + 0.2, y + h, zz + 0.55, 0.52, 0.32, 0.055, 0x2e535b);
          g.box(xx + 0.24, y + h + 0.01, zz + 0.2, 0.48, 0.025, 0.18, 0x657a7b);
        }
        if (b.furniture === 'chair') g.box(xx, y + 0.5, zz + d - 0.08, w, 0.5, 0.08, shade);
      }
      markLayer(c.y);
      continue;
    }
    if (b.category === 'prop') {
      propCellGeometry(g, b, c, x, y, z, shade);
      markLayer(c.y);
      continue;
    }
    if (
      c.role === 'roof' &&
      c.material === 'wood' &&
      b.roofLevel !== undefined &&
      c.y > b.roofLevel &&
      b.roofSpan
    ) {
      const roofX = c.x % b.roofSpan;
      const roofBottom = (b.roofLevel + 1) * CELL - origin.y;
      const left = roofBottom + (0.2 + Math.min(roofX, b.roofSpan - roofX) * 0.6) * CELL - y;
      const right = roofBottom + (0.2 + Math.min(roofX + 1, b.roofSpan - roofX - 1) * 0.6) * CELL - y;
      const roofColor = new THREE.Color(b.exterior?.roofColor ?? (b.kind === 'barn' ? 0x687b75 : 0xa76f53))
        .multiplyScalar(variation * (c.hp < 0.6 ? 0.73 : 1))
        .getHex();
      roofWedge(g, x, y, z, left, right, shade, frameVisible, roofColor);
    } else if (c.bounds) {
      const box = c.bounds;
      g.box(
        x + box.x * CELL,
        y + box.y * CELL,
        z + box.z * CELL,
        box.w * CELL,
        box.h * CELL,
        box.d * CELL,
        shade,
        frameVisible,
      );
    } else g.box(x, y, z, CELL, CELL, CELL, shade, frameVisible);
    if (
      detail &&
      visible[5] &&
      c.y <= 1 &&
      c.x === Math.floor(b.w / 2) &&
      ['pitched-house', 'semi-detached', 'rowhouse'].includes(b.kind)
    ) {
      g.box(x + 0.24, y, z - 0.04, 0.78, c.y === 0 ? CELL : 0.88, 0.05, 0x4f6e68);
      if (c.y === 0) g.box(x + 0.06, y, z - 0.48, CELL - 0.12, 0.14, 0.49, 0xc2c4b3);
    }
    if (b.kind === 'bridge') {
      if (c.y === 3 && c.z >= 1 && c.z <= 4 && c.x % 4 <= 1 && c.z === 3)
        g.box(x, y + CELL + 0.006, z + 0.1, CELL, 0.016, 0.12, 0xe6dac0);
      markLayer(c.y);
      continue;
    }
    const edgeFaces = [visible[0], visible[1], false, false, visible[4], visible[5]];
    if (c.material === 'glass' && detail) {
      const rim = c.windowTrim === undefined ? 0.045 : 0.14,
        tint = c.windowTrim ?? (b.kind === 'glass-office' ? 0xb3c9ca : 0xd0d6cc),
        shape = c.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 },
        xx = x + shape.x * CELL,
        yy = y + shape.y * CELL,
        zz = z + shape.z * CELL,
        w = shape.w * CELL,
        h = shape.h * CELL,
        d = shape.d * CELL;
      for (const side of [4, 5])
        if (edgeFaces[side] && shape.d <= shape.w) {
          const at = side === 5 ? zz - 0.02 : zz + d;
          facade.box(xx, yy, at, w, rim, 0.028, tint);
          facade.box(xx, yy + h - rim, at, w, rim, 0.028, tint);
          facade.box(xx, yy, at, rim, h, 0.028, tint);
          facade.box(xx + w - rim, yy, at, rim, h, 0.028, tint);
        }
      for (const side of [0, 1])
        if (edgeFaces[side] && shape.w <= shape.d) {
          const at = side === 1 ? xx - 0.02 : xx + w;
          facade.box(at, yy, zz, 0.028, rim, d, tint);
          facade.box(at, yy + h - rim, zz, 0.028, rim, d, tint);
          facade.box(at, yy, zz, 0.028, h, rim, tint);
          facade.box(at, yy, zz + d - rim, 0.028, h, rim, tint);
        }
    }
    if (
      c.role === 'roof' &&
      visible[2] &&
      !pitchedRoof &&
      !['hangar', 'grain-silo'].includes(b.kind) &&
      !['pitched-house', 'semi-detached', 'rowhouse', 'barn'].includes(b.kind)
    ) {
      const style = b.exterior;
      if (
        detail &&
        style &&
        c.y >= (b.roofLevel ?? b.h - 2) &&
        c.x === 2 + (style.rhythm % Math.max(1, b.w - 4)) &&
        c.z === Math.floor(b.d / 2)
      ) {
        // Equipment and clerestories are batched with their damageable roof.
        const height = 0.28 + style.roof * 0.14;
        g.box(x + 0.08, y + CELL, z + 0.08, 1.04, height, 1.04, style.trim);
        g.box(
          x + 0.14,
          y + CELL + height,
          z + 0.14,
          0.92,
          0.07,
          0.92,
          style.roof % 2 ? style.window : 0xc4cac1,
        );
      }
      for (const f of [0, 1, 4, 5])
        if (visible[f]) {
          if (f === 0 || f === 1)
            g.box(
              x + (f === 0 ? CELL - 0.12 : -0.04),
              y + CELL,
              z,
              0.16,
              0.24,
              CELL,
              style?.trim ?? 0xe0e1d4,
            );
          else
            g.box(
              x,
              y + CELL,
              z + (f === 4 ? CELL - 0.12 : -0.04),
              CELL,
              0.24,
              0.16,
              style?.trim ?? 0xe0e1d4,
            );
        }
    }
    if (
      !detail ||
      c.material === 'glass' ||
      c.material === 'metal' ||
      c.material === 'foliage' ||
      c.y === 0 ||
      c.y % 3 === 0 ||
      c.role === 'roof' ||
      c.role === 'beam' ||
      c.role === 'column'
    ) {
      markLayer(c.y);
      continue;
    }
    const house = ['pitched-house', 'semi-detached', 'rowhouse', 'barn'].includes(b.kind);
    const style = b.exterior;
    const width = style
      ? 0.52 + style.bays * 0.105
      : house
        ? 1.0
        : b.kind === 'masonry-highrise'
          ? 0.58
          : b.kind === 'warehouse'
            ? 0.86
            : 0.76;
    const pad = (CELL - width) / 2,
      window = style?.window ?? (b.kind === 'barn' ? 0x66594c : 0x506f74);
    // These windows and their frames stay in the parent's facade pass, and leave with that cell.
    const wy = c.y % 3 === 1 ? y + 0.35 : y;
    const wh = c.y % 3 === 1 ? CELL - 0.35 : 0.72;
    for (const side of [0, 1, 4, 5])
      if (visible[side] && (!house || (side >= 4 ? c.x : c.z) % 3 === 1)) {
        const bay = side >= 4 ? c.x : c.z;
        if (style && (bay + style.rhythm) % (style.bays + 1) === 0) {
          // Solid piers create a different readable bay rhythm per building.
          if (side >= 4)
            g.box(x + CELL * 0.37, y, side === 5 ? z - 0.06 : z + CELL, CELL * 0.26, CELL, 0.08, style.trim);
          else
            g.box(side === 1 ? x - 0.06 : x + CELL, y, z + CELL * 0.37, 0.08, CELL, CELL * 0.26, style.trim);
          continue;
        }
        if (side >= 4) {
          const zz = side === 5 ? z - 0.034 : z + CELL;
          g.box(x + pad, wy, zz, width, wh, 0.045, window);
          if (c.y % 3 === 1)
            g.box(x + pad - 0.05, wy - 0.1, zz - 0.04, width + 0.1, 0.1, 0.15, style?.trim ?? 0xe5e0d0);
          if (b.kind === 'masonry-highrise')
            g.box(x + pad + width / 2, wy, zz - 0.01, 0.048, wh, 0.08, 0xbaa892);
          if (c.y === 1 && ['corner-shop', 'shop', 'terrace', 'rowhouse'].includes(b.kind))
            g.box(
              x - 0.02,
              y + CELL - 0.08,
              side === 5 ? z - 0.68 : z + CELL,
              CELL + 0.04,
              0.16,
              0.68,
              b.accent,
            );
          if (
            ['modern-apartment', 'apartment', 'courtyard', 'semi-detached'].includes(b.kind) &&
            c.y % 3 === 1 &&
            c.x % 3 !== 0
          ) {
            const balconyZ = side === 5 ? z - 0.73 : z + CELL;
            g.box(x - 0.06, y - 0.04, balconyZ, CELL + 0.12, 0.13, 0.74, 0xe4e2d3);
            g.box(x - 0.06, y + 0.15, balconyZ + (side === 5 ? 0 : 0.68), CELL + 0.12, 0.34, 0.07, b.accent);
          }
        } else {
          const xx = side === 1 ? x - 0.034 : x + CELL;
          g.box(xx, wy, z + pad, 0.045, wh, width, window);
          if (c.y % 3 === 1)
            g.box(xx - 0.04, wy - 0.1, z + pad - 0.05, 0.15, 0.1, width + 0.1, style?.trim ?? 0xe5e0d0);
        }
        facadeFittings(g, b, c, x, y, z, side, pad, width, wy, wh);
      }
    markLayer(c.y);
  }
  flushLayer();
  for (const list of [frameLayers, facadeLayers, glassLayers])
    for (let y = 1; y < list.length; y++) list[y] = Math.max(list[y], list[y - 1]);
  const frameCount = frame.vertexCount,
    facadeCount = facade.vertexCount;
  yield* appendGeometry(frame, facade);
  const glassStart = frame.vertexCount;
  yield* appendGeometry(frame, glass);
  const clearStart = frame.vertexCount;
  yield* appendGeometry(frame, clearGlass);
  const geometry = yield* frame.geometrySteps(chunk * 256);
  geometry.clearGroups();
  if (glassStart) geometry.addGroup(0, glassStart, 0);
  if (glass.vertexCount) geometry.addGroup(glassStart, glass.vertexCount, 1);
  if (clearGlass.vertexCount) geometry.addGroup(clearStart, clearGlass.vertexCount, 2);
  geometry.userData.frameCount = frameCount;
  geometry.userData.frameLayers = frameLayers;
  geometry.userData.facadeStart = frameCount;
  geometry.userData.facadeLayers = facadeLayers;
  geometry.userData.glassStart = glassStart;
  geometry.userData.glassLayers = glassLayers;
  geometry.userData.facadeCount = facadeCount;
  geometry.userData.layers = frameLayers.map((n, y) => n + facadeLayers[y] + glassLayers[y]);
  return geometry;
}

/** A sloped roof cell clips the occupied voxel envelope; destruction exposes its cut face. */
function roofWedge(
  g: GeometryBuilder,
  x: number,
  y: number,
  z: number,
  a: number,
  b: number,
  color: number,
  faces: boolean[],
  surfaceColor = color,
): void {
  type Point = { x: number; y: number };
  const square: Point[] = [
    { x: 0, y: 0 },
    { x: CELL, y: 0 },
    { x: CELL, y: CELL },
    { x: 0, y: CELL },
  ];
  const polygon: Point[] = [];
  const signed = (p: Point) => a + ((b - a) * p.x) / CELL - p.y;
  for (let i = 0; i < square.length; i++) {
    const p = square[i],
      q = square[(i + 1) % square.length],
      dp = signed(p),
      dq = signed(q);
    if (dp >= -1e-7) polygon.push(p);
    if (dp >= 0 !== dq >= 0) {
      const t = dp / (dp - dq);
      polygon.push({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });
    }
  }
  if (polygon.length < 3) return;
  const point = (p: Point, zz: number) => ({ x: x + p.x, y: y + p.y, z: zz });
  for (let i = 0; i < polygon.length; i++) {
    const p = polygon[i],
      q = polygon[(i + 1) % polygon.length];
    const dx = q.x - p.x,
      dy = q.y - p.y;
    if (Math.hypot(dx, dy) < 1e-7) continue;
    const left = Math.abs(p.x) + Math.abs(q.x) < 1e-6;
    const right = Math.abs(p.x - CELL) + Math.abs(q.x - CELL) < 1e-6;
    const bottom = Math.abs(p.y) + Math.abs(q.y) < 1e-6;
    const top = Math.abs(p.y - CELL) + Math.abs(q.y - CELL) < 1e-6;
    if ((left && !faces[1]) || (right && !faces[0]) || (bottom && !faces[3]) || (top && !faces[2])) continue;
    const n = new THREE.Vector3(dy, -dx, 0).normalize();
    g.quad(
      point(p, z),
      point(q, z),
      point(q, z + CELL),
      point(p, z + CELL),
      n,
      n.y > 0.1 ? surfaceColor : color,
    );
  }
  for (let i = 1; i < polygon.length - 1; i++) {
    if (faces[4])
      g.quad(
        point(polygon[0], z + CELL),
        point(polygon[i], z + CELL),
        point(polygon[i + 1], z + CELL),
        point(polygon[i + 1], z + CELL),
        { x: 0, y: 0, z: 1 },
        color,
      );
    if (faces[5])
      g.quad(
        point(polygon[0], z),
        point(polygon[i + 1], z),
        point(polygon[i], z),
        point(polygon[i], z),
        { x: 0, y: 0, z: -1 },
        color,
      );
  }
}
function propCellGeometry(
  g: GeometryBuilder,
  b: Building,
  c: Cell,
  x: number,
  y: number,
  z: number,
  color: number,
): void {
  if (b.kind === 'tree') {
    if (c.material === 'wood') {
      g.box(x + 0.4, y, z + 0.4, 0.45, CELL, 0.45, color);
      if (c.y >= b.h - 3) {
        g.box(x, y + 0.5, z + 0.48, CELL, 0.2, 0.24, color);
        g.box(x + 0.48, y + 0.5, z, 0.24, 0.2, CELL, color);
      }
    } else {
      g.box(x + 0.06, y + 0.08, z + 0.06, CELL - 0.12, CELL - 0.16, CELL - 0.12, color);
      g.box(x - 0.12, y + 0.26, z + 0.18, CELL + 0.22, CELL - 0.5, CELL - 0.36, color);
    }
  } else if (b.kind === 'lamp') {
    if (c.x === 0) g.box(x + 0.53, y, z + 0.53, 0.18, CELL, 0.18, color);
    if (c.y === 0) g.box(x + 0.38, y, z + 0.38, 0.48, 0.22, 0.48, 0x88938b);
    if (c.y === 3) {
      g.box(x + (c.x ? 0 : 0.53), y + 0.6, z + 0.51, c.x ? 0.9 : 0.72, 0.15, 0.24, color);
      if (c.x) g.box(x + 0.1, y + 0.54, z + 0.47, 0.65, 0.07, 0.34, 0xffe4ac);
    }
  } else if (b.kind === 'bench') {
    g.box(x, y + 0.48, z + 0.24, CELL, 0.13, 0.53, color);
    g.box(x, y + 0.65, z + 0.68, CELL, 0.4, 0.11, color);
    for (const dx of [0.12, 0.96]) g.box(x + dx, y, z + 0.29, 0.12, 0.5, 0.39, 0x6c7c70);
  } else if (b.kind === 'fence') {
    g.box(x + 0.51, y, z + 0.5, 0.19, 1.1, 0.19, color);
    for (const dy of [0.32, 0.8]) g.box(x, y + dy, z + 0.53, CELL, 0.12, 0.12, color);
  } else if (b.kind === 'paving') {
    g.box(x + 0.02, y, z + 0.02, CELL - 0.04, 0.12, CELL - 0.04, color);
  } else if (b.kind === 'planter') {
    g.box(x + 0.05, y, z + 0.13, CELL - 0.1, c.material === 'foliage' ? 0.65 : CELL, CELL - 0.26, color);
  } else if (b.kind === 'shelter') {
    if (c.role === 'column') g.box(x + 0.45, y, z + 0.45, 0.35, CELL, 0.35, color);
    else g.box(x - 0.04, y, z - 0.04, CELL + 0.08, 0.28, CELL + 0.08, color);
  } else if (b.kind === 'fountain') {
    g.box(x + 0.02, y, z + 0.02, CELL - 0.04, c.y === 0 ? 0.46 : CELL, CELL - 0.04, color);
    if (c.y === 0 && c.x > 0 && c.x < 4 && c.z > 0 && c.z < 4)
      g.box(x, y + 0.47, z, CELL, 0.035, CELL, 0x6ba5ac);
  } else g.box(x, y, z, CELL, CELL, CELL, color);
}
export function personGeometry(worker = false, variant = 0): THREE.BufferGeometry {
  const g = new GeometryBuilder();
  g.box(-0.18, 0.56, -0.12, 0.36, 0.55, 0.24, worker ? 0xefb648 : 0xffffff);
  g.box(-0.14, 1.12, -0.12, 0.28, 0.28, 0.25, [0xd4a17d, 0x9a7156, 0xe4bd9a, 0xbb8762][variant % 4]);
  g.box(-0.17, 0, -0.1, 0.12, 0.58, 0.19, 0x405560);
  g.box(0.05, 0, -0.1, 0.12, 0.58, 0.19, 0x405560);
  g.box(-0.26, 0.65, -0.08, 0.08, 0.4, 0.16, worker ? 0xefb648 : 0xffffff);
  g.box(0.18, 0.65, -0.08, 0.08, 0.4, 0.16, worker ? 0xefb648 : 0xffffff);
  g.box(
    -0.16,
    1.38,
    -0.13,
    0.32,
    0.1,
    0.28,
    worker ? 0xffd45a : [0x665344, 0x393b35, 0xae8959, 0x736955][variant % 4],
  );
  if (!worker && variant % 3 === 1) g.box(-0.15, 0.6, 0.13, 0.3, 0.45, 0.16, 0x567a7d);
  if (!worker && variant % 4 === 2) g.box(-0.21, 0.5, -0.14, 0.42, 0.21, 0.3, 0xffffff);
  const geometry = g.geometry();
  geometry.scale(1 + ((variant % 3) - 1) * 0.1, worker ? 1 : 0.88 + (variant % 5) * 0.045, 1);
  return geometry;
}
export function carGeometry(): THREE.BufferGeometry {
  const g = new GeometryBuilder();
  g.box(-0.8, 0.42, -1.5, 1.6, 0.52, 3, 0xffffff);
  g.box(-0.68, 0.94, -0.72, 1.36, 0.52, 1.55, 0xcbd9d7);
  g.box(-0.6, 1, -0.745, 1.2, 0.37, 0.035, 0x385767);
  g.box(-0.6, 1, 0.82, 1.2, 0.37, 0.035, 0x385767);
  for (const x of [-0.88, 0.68]) for (const z of [-1.05, 0.8]) g.box(x, 0.2, z, 0.2, 0.5, 0.55, 0x34434b);
  g.box(-0.7, 0.6, -1.52, 0.35, 0.2, 0.04, 0xffe3a1);
  g.box(0.35, 0.6, -1.52, 0.35, 0.2, 0.04, 0xffe3a1);
  return g.geometry();
}
export type VehicleKind =
  | MilitaryVehicleKind
  | RoadFamily
  | 'police'
  | 'fire'
  | 'boat'
  | 'ambulance'
  | 'tow'
  | 'train'
  | 'airport-fuel'
  | 'airport-baggage';
/** Origin is ground contact, forward is -Z. White body panels support instance tinting. */
export function vehicleGeometry(kind: VehicleKind): THREE.BufferGeometry {
  return isMilitaryVehicle(kind)
    ? militaryVehicleGeometry(kind)
    : isRoadFamily(kind)
      ? roadVehicleGeometry(kind)
      : serviceVehicleGeometry(kind);
}
function serviceVehicleGeometry(kind: VehicleKind): THREE.BufferGeometry {
  if (kind === 'car') return carGeometry();
  const g = new GeometryBuilder();
  if (kind === 'airport-fuel' || kind === 'airport-baggage') {
    const fuel = kind === 'airport-fuel';
    g.box(-0.85, 0.28, -2.3, 1.7, 0.22, 4.6, 0x647773);
    g.box(-0.78, 0.5, -2.2, 1.56, 1.45, 1.3, 0xffffff);
    g.box(-0.68, 1.15, -2.23, 1.36, 0.59, 0.035, 0x4c7381);
    for (const x of [-0.82, 0.78]) g.box(x, 1.15, -2.05, 0.04, 0.59, 0.9, 0x4c7381);
    for (const x of [-0.66, 0.43]) g.box(x, 0.66, -2.26, 0.22, 0.16, 0.04, 0xffe8af);
    g.box(-0.2, 1.96, -1.68, 0.4, 0.12, 0.25, 0xd6a557);
    for (const x of [-0.92, 0.68])
      for (const z of [-1.85, 1.38]) {
        g.box(x, 0.04, z, 0.24, 0.6, 0.65, 0x354441);
        g.box(x - 0.01, 0.22, z + 0.17, 0.26, 0.24, 0.3, 0xa8b1a1);
      }
    if (fuel) {
      g.box(-0.73, 0.59, -0.66, 1.46, 1.12, 2.75, 0xe2e0c8);
      g.box(-0.58, 1.71, -0.5, 1.16, 0.22, 2.44, 0xc9d2c4);
      for (const z of [-0.3, 1.3]) g.box(-0.76, 0.7, z, 1.52, 1.06, 0.13, 0x748781);
      g.box(-0.86, 0.73, 0.14, 0.12, 0.8, 1.2, 0x525f5c);
      g.box(0.4, 1.94, 0.75, 0.26, 0.12, 0.35, 0x697772);
    } else {
      g.box(-0.8, 0.57, -0.7, 1.6, 0.14, 2.8, 0xb0bcb0);
      for (const x of [-0.8, 0.7]) g.box(x, 0.7, -0.7, 0.1, 0.8, 2.8, 0xe7e0bb);
      for (const [i, z] of [-0.5, 0.42, 1.34].entries()) {
        g.box(-0.64, 0.72, z, 0.56, 0.6, 0.62, [0x9a8a72, 0x637f87, 0xb5957d][i]);
        g.box(0.05, 0.72, z, 0.56, 0.45, 0.62, 0x718b78);
      }
    }
    return g.geometry();
  }
  if (kind === 'train') {
    g.box(-1.3, 0.3, -5.8, 2.6, 0.22, 11.6, 0xe7e7d9);
    for (const x of [-1.3, 1.22]) for (const z of [-5.7, 0.68]) g.box(x, 0.52, z, 0.08, 2.6, 5.02, 0xffffff);
    for (const z of [-5.8, 5.65]) g.box(-1.3, 0.52, z, 2.6, 2.6, 0.15, 0xffffff);
    for (const z of [-4.8, -3.4, -2, 1.2, 2.6, 4])
      for (const x of [-1.1, 0.5]) g.box(x, 0.52, z, 0.6, 0.65, 0.7, 0x5d9290);
    g.box(-1.28, 3.12, -5.65, 2.56, 0.2, 11.3, 0xadbfb9);
    for (const x of [-1.31, 1.27]) {
      g.box(x, 1.08, -5.65, 0.04, 0.25, 11.3, 0x448c86);
      for (const z of [-5.1, -3.8, -2.5, 1.5, 2.8, 4.1]) g.box(x, 1.78, z, 0.04, 1.02, 1.02, 0x456b75);
      if (x < 0) g.box(x, 0.52, -0.65, 0.04, 2.6, 1.3, 0x8baba9);
    }
    for (const z of [-4.7, 3.5]) {
      g.box(-1.1, 0.12, z, 2.2, 0.5, 1.1, 0x394b4d);
      g.box(-0.8, 0.32, z, 1.6, 0.24, 1.1, 0x8d9c94);
    }
    for (const z of [-5.82, 5.78]) {
      g.box(-1.05, 1.7, z, 2.1, 1.12, 0.04, 0x426e78);
      g.box(-0.8, 1.35, z, 0.3, 0.18, 0.05, 0xffe1ac);
      g.box(0.5, 1.35, z, 0.3, 0.18, 0.05, 0xffe1ac);
    }
    g.box(-0.3, 0.62, -6.22, 0.6, 0.3, 12.44, 0x607b78);
    return g.geometry();
  }
  if (kind === 'boat') {
    g.box(-0.94, 0.05, -2.3, 1.88, 0.36, 4.6, 0xede9d9);
    g.box(-1.1, 0.34, -1.9, 2.2, 0.48, 3.8, 0xffffff);
    g.box(-0.9, 0.8, -1.5, 1.8, 0.12, 3.0, 0xc5a67b);
    g.box(-0.72, 0.94, -0.45, 1.44, 1.05, 1.6, 0xdce6df);
    g.box(-0.67, 1.2, -0.48, 1.34, 0.62, 0.07, 0x4c7b85);
    g.box(-0.85, 1.98, -0.57, 1.7, 0.12, 1.92, 0xffffff);
    return g.geometry();
  }
  if (kind === 'motorcycle') {
    for (const z of [-0.73, 0.59]) {
      g.box(-0.15, 0.1, z, 0.3, 0.61, 0.52, 0x344145);
      g.box(-0.11, 0.22, z + 0.09, 0.22, 0.37, 0.33, 0xa5b1ab);
    }
    g.box(-0.29, 0.6, -0.37, 0.58, 0.42, 1.05, 0xffffff);
    g.box(-0.32, 0.97, -0.03, 0.64, 0.17, 0.7, 0x394f53);
    g.box(-0.4, 1.03, -0.68, 0.8, 0.12, 0.1, 0x77918e);
    g.box(-0.23, 1.09, -0.16, 0.46, 0.57, 0.33, 0x657974);
    g.box(-0.2, 1.68, -0.22, 0.4, 0.38, 0.38, 0xe7ddbf);
    g.box(-0.17, 1.71, -0.23, 0.34, 0.14, 0.04, 0x425b61);
    return g.geometry();
  }
  const bus = kind === 'bus',
    truck = kind === 'truck' || kind === 'tow',
    fire = kind === 'fire';
  const length = bus ? 7.5 : truck || fire ? 6.3 : kind === 'van' || kind === 'ambulance' ? 4.7 : 3.8;
  const width = bus || truck || fire ? 2.1 : 1.8;
  const half = width / 2,
    front = -length / 2;
  g.box(-half, 0.44, front, width, 0.72, length, 0xffffff);
  if (truck) {
    g.box(-half + 0.05, 1.16, front + 0.12, width - 0.1, 1.53, 1.8, 0xffffff);
    g.box(-half, 1.12, front + 2.12, width, kind === 'tow' ? 0.12 : 1.84, length - 2.22, 0xb6c5bd);
    if (kind === 'tow') {
      g.box(-0.65, 1.3, front + 2, 0.15, 1.4, 0.15, 0x6a7c78);
      g.box(0.5, 1.3, front + 2, 0.15, 1.4, 0.15, 0x6a7c78);
      g.box(-0.65, 2.6, front + 2, 1.3, 0.12, 0.15, 0xd3b572);
    } else
      for (let zz = front + 2.4; zz < -front - 0.2; zz += 0.55)
        g.box(-half - 0.02, 1.2, zz, 0.035, 1.65, 0.08, 0x98aca4);
  } else {
    g.box(-half + 0.09, 1.16, front + 0.34, width - 0.18, bus || fire ? 1.5 : 1.0, length - 0.65, 0xffffff);
    if (bus)
      for (let zz = front + 0.48; zz < -front - 0.6; zz += 1.1)
        for (const xx of [-half - 0.004, half - 0.04]) g.box(xx, 1.58, zz, 0.055, 0.81, 0.88, 0x50747b);
    if (fire) {
      g.box(-0.83, 2.68, front + 1.25, 0.22, 0.19, length - 1.6, 0xcbd2c9);
      g.box(0.61, 2.68, front + 1.25, 0.22, 0.19, length - 1.6, 0xcbd2c9);
      for (let zz = front + 1.4; zz < -front - 0.2; zz += 0.4)
        g.box(-0.7, 2.68, zz, 1.4, 0.13, 0.1, 0xb0beb6);
      for (let zz = front + 2.1; zz < -front - 0.35; zz += 1.1)
        for (const xx of [-half - 0.004, half - 0.04]) g.box(xx, 1.27, zz, 0.055, 1.05, 0.87, 0xaab9b2);
    }
  }
  g.box(-half + 0.2, 1.45, front + 0.08, width - 0.4, bus || truck || fire ? 0.91 : 0.61, 0.08, 0x476c76);
  for (const xx of [-half - 0.12, half - 0.17])
    for (const zz of [front + 0.66, -front - 1.28]) {
      g.box(xx, 0.12, zz, 0.29, bus || truck || fire ? 0.83 : 0.67, 0.76, 0x354447);
      g.box(xx - 0.01, 0.31, zz + 0.16, 0.31, 0.38, 0.42, 0xa5b4ae);
    }
  for (const xx of [-half + 0.11, half - 0.49]) g.box(xx, 0.68, front - 0.03, 0.36, 0.21, 0.05, 0xffe4b0);
  if (kind === 'ambulance') {
    for (const side of [-half - 0.015, half - 0.025]) {
      g.box(side, 1.2, front + 1, 0.04, 0.22, length - 1.2, 0x5b9c9d);
      g.box(side, 1.43, 0.2, 0.045, 0.6, 0.22, 0x65aaa3);
      g.box(side, 1.63, -0.01, 0.045, 0.2, 0.64, 0x65aaa3);
    }
    g.box(-half + 0.08, 2.12, front + 1.5, width - 0.16, 0.28, length - 1.8, 0xffffff);
  }
  if (kind === 'police' || fire || kind === 'ambulance') {
    const yy = fire ? 2.73 : 2.21;
    g.box(-0.65, yy, front + 0.75, 1.3, 0.12, 0.4, 0x4c6871);
    g.box(-0.64, yy + 0.12, front + 0.76, 0.55, 0.2, 0.38, 0x7faad4);
    g.box(0.09, yy + 0.12, front + 0.76, 0.55, 0.2, 0.38, 0xd88671);
  }
  return g.geometry();
}

export function trainDoorGeometry(sign: number): THREE.BufferGeometry {
  const g = new GeometryBuilder(),
    z = sign > 0 ? 0 : -0.65;
  g.box(1.3, 0.52, z, 0.045, 2.58, 0.65, 0xc0d3cc);
  g.box(1.35, 1.65, z + 0.08, 0.025, 1.0, 0.49, 0x466f77);
  return g.geometry();
}
export const animalGeometry = wildlifeGeometry;
export function bulldozerGeometry(): THREE.BufferGeometry {
  const g = new GeometryBuilder();
  g.box(-1.05, 0.42, -1.5, 2.1, 0.76, 3, 0xd79c32);
  for (const x of [-1.48, 0.99]) {
    g.box(x, 0.12, -1.48, 0.5, 0.63, 3.2, 0x3c4b4c);
    for (let z = -1.42; z < 1.6; z += 0.26) g.box(x - 0.035, 0.54, z, 0.57, 0.1, 0.12, 0x65706a);
  }
  g.box(-0.67, 1.1, -0.12, 1.34, 1.28, 1.28, 0x425f61);
  for (const x of [-0.77, 0.63]) for (const z of [-0.22, 1.05]) g.box(x, 1.06, z, 0.14, 1.48, 0.14, 0xebba51);
  g.box(-0.92, 2.5, -0.34, 1.84, 0.16, 1.68, 0xf0c55e);
  g.box(0.7, 1.18, -1.19, 0.18, 1.02, 0.19, 0x454e48);
  for (const x of [-0.92, 0.76]) g.box(x, 0.47, -2.55, 0.16, 0.23, 1.6, 0xc28731);
  g.box(-1.92, 0.18, -2.7, 3.84, 1.02, 0.3, 0xf3c453);
  g.box(-1.96, 0.14, -2.79, 3.92, 0.18, 0.39, 0x59645e);
  return g.geometry();
}
